import React, { useState, useContext, useCallback, useEffect } from "react";
import InfiniteLoader from "react-window-infinite-loader";
import { useVirtualScrolling } from "../components/ReactTable/useVirtualScrolling";
import { PropTypes } from "prop-types";

const VirtualScrollingContext = React.createContext();

const VirtualScrollingProvider = ({
  fetchData,
  initialPage = 1,
  pageSize = 10,
  children
}) => {
  const [page, setPage] = useState({ number: initialPage, size: pageSize });

  useEffect(() => {
    setPage({ ...page, number: 1 });
  }, [fetchData]);

  const fetchMoreItems = useCallback(() => fetchData(page), [page]);

  const {
    results: items,
    hasNextPage,
    isNextPageLoading,
    fetchCount,
    hasResults,
    totalCount
  } = useVirtualScrolling({
    fetchData: fetchMoreItems,
    page: page.number
  });

  const loadMoreItems = startIndex => {
    setPage({ ...page, number: Math.floor(startIndex / pageSize) + 1 });
  };

  const itemCount = hasNextPage ? items.length + 1 : items.length;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !hasNextPage || index < items.length;

  const contextValue = {
    itemCount,
    loadMoreItems,
    isItemLoaded,
    items,
    isNextPageLoading,
    fetchCount,
    hasResults,
    totalCount
  };
  return (
    <VirtualScrollingContext.Provider value={contextValue}>
      {children(contextValue)}
    </VirtualScrollingContext.Provider>
  );
};

const VirtualScrollingInfiniteLoader = ({ children, infiniteLoaderRef }) => {
  const { itemCount, loadMoreItems, isItemLoaded } = useContext(
    VirtualScrollingContext
  );

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={loadMoreItems}
      ref={infiniteLoaderRef}
    >
      {children}
    </InfiniteLoader>
  );
};

export default VirtualScrollingContext;

export { VirtualScrollingProvider, VirtualScrollingInfiniteLoader };

VirtualScrollingProvider.propTypes = {
  fetchData: PropTypes.func,
  initialPage: PropTypes.number,
  pageSize: PropTypes.number,
  children: PropTypes.func
};
